import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, Clock, Fuel, RefreshCw, XCircle } from "lucide-react";
import { AuthContext } from "../Context/AuthContext";
import { useToast } from "../Context/ToastContext";
import { resolveEmployeeIdFromProfile } from "../../components/common/employeeUtils";
import {
  apiMessage,
  createFuelBill,
  deleteFuelBill,
  duplicateFromError,
  getEmployeeFuelBills,
} from "./allowancesApi";
import { formatDate, formatMoney, mediaUrl } from "./shared";
import "../../styles/employee-fuel.css";

const POLL_MS = 8000;
const EMPTY_BILLS = [];
const MAX_BILL_BYTES = 10 * 1024 * 1024;
const FILTERS = [
  { id: "ALL", label: "All" },
  { id: "PENDING", label: "Pending" },
  { id: "APPROVED", label: "Approved" },
  { id: "REJECTED", label: "Rejected" },
];

function todayISO() {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
}

function currentMonthKey() {
  return todayISO().slice(0, 7);
}

function statusKey(bill) {
  return String(bill?.status || "PENDING").toUpperCase();
}

function monthLabel(dateValue) {
  const raw = String(dateValue || "").slice(0, 10);
  if (raw.length < 7) return "that month";
  const [year, month] = raw.split("-").map(Number);
  if (!year || !month) return "that month";
  return new Date(year, month - 1, 1).toLocaleDateString("en-IN", { month: "long", year: "numeric" });
}

export function employeeFuelQueryKey(employeeId) {
  return ["employee-fuel-bills", employeeId || "self"];
}

export function useEmployeeFuelBills(employeeId, { enabled = true } = {}) {
  return useQuery({
    queryKey: employeeFuelQueryKey(employeeId),
    queryFn: () => getEmployeeFuelBills(employeeId),
    enabled: Boolean(enabled && employeeId),
    refetchInterval: POLL_MS,
    refetchIntervalInBackground: false,
    staleTime: 4000,
    refetchOnWindowFocus: true,
  });
}

function Cycle({ status }) {
  const key = String(status || "PENDING").toUpperCase();
  const decided = key === "APPROVED" || key === "REJECTED";
  const steps = [
    { id: "raised", label: "Raised", state: "done" },
    { id: "review", label: "Pending review", state: decided ? "done" : "current" },
    {
      id: "decision",
      label: key === "REJECTED" ? "Rejected" : key === "APPROVED" ? "Approved" : "Decision",
      state: key === "APPROVED" ? "approved" : key === "REJECTED" ? "rejected" : "upcoming",
    },
  ];
  return (
    <ol className="ef-cycle" aria-label="Request status">
      {steps.map((step) => (
        <li key={step.id} className={`ef-cycle__step is-${step.state}`}>
          <span className="ef-cycle__dot" aria-hidden />
          <span>{step.label}</span>
        </li>
      ))}
    </ol>
  );
}

function StatusMark({ status }) {
  const key = String(status || "PENDING").toUpperCase();
  if (key === "APPROVED") {
    return (
      <span className="ef-mark ef-mark--approved">
        <CheckCircle2 size={16} /> Approved
      </span>
    );
  }
  if (key === "REJECTED") {
    return (
      <span className="ef-mark ef-mark--rejected">
        <XCircle size={16} /> Rejected
      </span>
    );
  }
  return (
    <span className="ef-mark ef-mark--pending">
      <Clock size={16} /> Pending
    </span>
  );
}

function Outcome({ bill }) {
  const key = statusKey(bill);
  if (key === "APPROVED") {
    return (
      <div className="ef-outcome ef-outcome--approved" role="status">
        <strong>Approved</strong>
        <p>
          HR or Admin verified this bill. The amount is added to your salary for {monthLabel(bill.bill_date)} when that month&apos;s payroll is processed.
        </p>
      </div>
    );
  }
  if (key === "REJECTED") {
    return (
      <div className="ef-outcome ef-outcome--rejected" role="status">
        <strong>Rejected</strong>
        <p>{bill.rejection_reason || "No reason was recorded. Contact HR if you need more detail."}</p>
      </div>
    );
  }
  return (
    <div className="ef-outcome ef-outcome--pending" role="status">
      <strong>Waiting for verification</strong>
      <p>HR or Admin will check the bill, bill number, and date, then approve or reject it.</p>
    </div>
  );
}

export function EmployeeFuelHomeCard() {
  const { user } = useContext(AuthContext);
  const employeeId = resolveEmployeeIdFromProfile(user);
  const fuelQuery = useEmployeeFuelBills(employeeId);
  const payload = fuelQuery.data;
  const bills = payload?.bills ?? EMPTY_BILLS;
  if (!employeeId) return null;
  if (payload && payload.enabled === false && bills.length === 0) return null;
  if (fuelQuery.isError && !bills.length) return null;

  const latest = bills.slice(0, 3);
  const pending = bills.filter((bill) => statusKey(bill) === "PENDING").length;

  return (
    <section className="ef-home" aria-label="Fuel reimbursement">
      <div className="ef-home__head">
        <h2>
          <Fuel size={16} /> Fuel reimbursement
        </h2>
        <span className={`ef-live${fuelQuery.isFetching ? " is-on" : ""}`}>
          <RefreshCw size={14} />
          {fuelQuery.isFetching ? "Updating" : "Live"}
        </span>
      </div>
      <p className="ef-card__lead">
        {pending
          ? `${pending} request${pending === 1 ? "" : "s"} waiting for HR or Admin.`
          : "Raise a bill and watch it move from pending to approved or rejected."}
      </p>
      {latest.length ? (
        <ul className="ef-home__list">
          {latest.map((bill) => {
            const key = statusKey(bill);
            return (
              <li key={bill.id}>
                <StatusMark status={key} />
                <div>
                  <strong>{formatMoney(bill.amount)}</strong>
                  <p>
                    {bill.bill_number || "No bill number"} · {formatDate(bill.bill_date)}
                  </p>
                  {key === "REJECTED" ? (
                    <p className="ef-home__reason">{bill.rejection_reason || "Rejected without a recorded reason."}</p>
                  ) : null}
                  {key === "APPROVED" ? (
                    <p>Approved. This amount is added to your salary for {monthLabel(bill.bill_date)}.</p>
                  ) : null}
                </div>
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="ef-empty">{fuelQuery.isLoading ? "Loading fuel requests…" : "No fuel requests yet."}</p>
      )}
      <Link className="ef-btn ef-btn--primary" to="/my-fuel">
        {pending || bills.length ? "Open fuel requests" : "Raise a fuel request"}
      </Link>
    </section>
  );
}

export default function EmployeeFuelRequestPage() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { user } = useContext(AuthContext);
  const employeeId = resolveEmployeeIdFromProfile(user);
  const fuelQuery = useEmployeeFuelBills(employeeId);
  const seenRef = useRef(null);

  const [filter, setFilter] = useState("ALL");
  const [saving, setSaving] = useState(false);
  const [cancellingId, setCancellingId] = useState(null);
  const [confirmCancelId, setConfirmCancelId] = useState(null);
  const [duplicateExisting, setDuplicateExisting] = useState(null);
  const [fileKey, setFileKey] = useState(0);
  const [form, setForm] = useState({
    bill_date: todayISO(),
    bill_number: "",
    amount: "",
    remarks: "",
    attachment: null,
  });

  const payload = fuelQuery.data;
  const bills = payload?.bills ?? EMPTY_BILLS;
  const enabled = Boolean(payload?.enabled);
  const eligible = Boolean(payload?.eligible);

  useEffect(() => {
    const rows = payload?.bills || [];
    const next = {};
    rows.forEach((bill) => {
      next[bill.id] = statusKey(bill);
    });
    const previous = seenRef.current;
    seenRef.current = next;
    if (!previous) return;
    rows.forEach((bill) => {
      const before = previous[bill.id];
      const now = statusKey(bill);
      if (before !== "PENDING" || before === now) return;
      const label = bill.bill_number ? `Bill ${bill.bill_number}` : "Your fuel bill";
      if (now === "APPROVED") {
        toast.success(`${label} was approved and will be added to your salary.`);
      } else if (now === "REJECTED") {
        toast.error(`${label} was rejected. ${bill.rejection_reason || "See the reason on this page."}`);
      }
    });
  }, [payload, toast]);

  const counts = useMemo(() => {
    const month = currentMonthKey();
    let pending = 0;
    let approvedMonth = 0;
    let rejected = 0;
    bills.forEach((bill) => {
      const key = statusKey(bill);
      if (key === "PENDING") pending += 1;
      if (key === "REJECTED") rejected += 1;
      if (key === "APPROVED" && String(bill.bill_date || "").startsWith(month)) {
        approvedMonth += Number(bill.amount) || 0;
      }
    });
    return { pending, approvedMonth, rejected };
  }, [bills]);

  const visibleBills = useMemo(() => {
    if (filter === "ALL") return bills;
    return bills.filter((bill) => statusKey(bill) === filter);
  }, [bills, filter]);

  const limit = payload?.monthly_limit;
  const remaining = limit == null ? null : Math.max(0, Number(limit) - counts.approvedMonth);
  const canRaise = enabled && eligible && Boolean(employeeId);

  function setField(key, value) {
    setForm((current) => ({ ...current, [key]: value }));
    setDuplicateExisting(null);
  }

  async function submitRequest(allowDuplicate = false) {
    if (!employeeId) {
      toast.error("Your employee profile is missing. Sign in again and retry.");
      return;
    }
    const amount = Number(form.amount);
    if (!form.bill_date) {
      toast.error("Bill date is required.");
      return;
    }
    if (!String(form.bill_number || "").trim()) {
      toast.error("Bill number is required.");
      return;
    }
    if (!Number.isFinite(amount) || amount <= 0) {
      toast.error("Enter a bill amount greater than 0.");
      return;
    }
    if (form.attachment && form.attachment.size > MAX_BILL_BYTES) {
      toast.error("The file is too large. Use a PDF, JPG, or PNG under 10 MB.");
      return;
    }

    setSaving(true);
    try {
      const body = new FormData();
      body.append("bill_date", form.bill_date);
      body.append("amount", String(amount));
      body.append("bill_number", form.bill_number.trim());
      if (form.remarks.trim()) body.append("remarks", form.remarks.trim());
      if (form.attachment) body.append("attachment", form.attachment);
      if (allowDuplicate) body.append("allow_duplicate", "true");
      await createFuelBill(employeeId, body);
      setForm({
        bill_date: todayISO(),
        bill_number: "",
        amount: "",
        remarks: "",
        attachment: null,
      });
      setDuplicateExisting(null);
      setFileKey((value) => value + 1);
      toast.success("Fuel request raised. It is pending HR or Admin verification.");
      await queryClient.invalidateQueries({ queryKey: employeeFuelQueryKey(employeeId) });
    } catch (err) {
      const existing = duplicateFromError(err);
      if (existing) {
        setDuplicateExisting(existing);
        return;
      }
      toast.error(apiMessage(err, "Unable to raise the fuel request."));
    } finally {
      setSaving(false);
    }
  }

  async function cancelRequest(billId) {
    setCancellingId(billId);
    try {
      await deleteFuelBill(billId);
      toast.success("Pending fuel request cancelled.");
      setConfirmCancelId(null);
      await queryClient.invalidateQueries({ queryKey: employeeFuelQueryKey(employeeId) });
    } catch (err) {
      toast.error(apiMessage(err, "Unable to cancel this request."));
    } finally {
      setCancellingId(null);
    }
  }

  return (
    <div className="ef-page">
      <header className="ef-header">
        <div>
          <h1>
            <Fuel size={22} /> Fuel reimbursement
          </h1>
          <p>Raise a fuel bill and follow it from pending review through approval or rejection.</p>
        </div>
        <div className={`ef-live${fuelQuery.isFetching ? " is-on" : ""}`}>
          <RefreshCw size={14} />
          {fuelQuery.isFetching ? "Updating" : "Live"}
        </div>
      </header>

      {!employeeId ? (
        <section className="ef-banner ef-banner--warn" role="alert">
          This login is not linked to an employee record, so a fuel request cannot be raised.
        </section>
      ) : null}

      {fuelQuery.isError ? (
        <section className="ef-banner ef-banner--warn" role="alert">
          {apiMessage(fuelQuery.error, "Unable to load your fuel requests.")}
          <button type="button" onClick={() => fuelQuery.refetch()}>Retry</button>
        </section>
      ) : null}

      {payload && !enabled ? (
        <section className="ef-banner">
          Fuel reimbursement is turned off for your organisation. Existing decisions stay visible below.
        </section>
      ) : null}

      {payload && enabled && !eligible ? (
        <section className="ef-banner">
          You are not marked eligible for fuel reimbursement. Ask HR to enable it on your employee record. Earlier requests still show their status here.
        </section>
      ) : null}

      <section className="ef-stats" aria-label="Fuel request totals">
        <article>
          <span>Pending</span>
          <strong>{fuelQuery.isLoading ? "—" : counts.pending}</strong>
          <small>Waiting for HR or Admin</small>
        </article>
        <article>
          <span>Approved this month</span>
          <strong>{fuelQuery.isLoading ? "—" : formatMoney(counts.approvedMonth)}</strong>
          <small>Added to salary after payroll</small>
        </article>
        <article>
          <span>Rejected</span>
          <strong>{fuelQuery.isLoading ? "—" : counts.rejected}</strong>
          <small>Reason is shown on each bill</small>
        </article>
        <article>
          <span>Monthly limit left</span>
          <strong>{fuelQuery.isLoading ? "—" : remaining == null ? "No cap" : formatMoney(remaining)}</strong>
          <small>{limit == null ? "HR has not set a cap" : `Limit ${formatMoney(limit)}`}</small>
        </article>
      </section>

      <div className="ef-layout">
        <section className="ef-card">
          <h2>Raise a request</h2>
          <p className="ef-card__lead">Enter the bill date, bill number, and amount. HR or Admin verifies these before the amount can be paid.</p>
          <form
            className="ef-form"
            onSubmit={(event) => {
              event.preventDefault();
              submitRequest(false);
            }}
          >
            <label>
              Bill date
              <input
                type="date"
                value={form.bill_date}
                max={todayISO()}
                required
                disabled={!canRaise || saving}
                onChange={(event) => setField("bill_date", event.target.value)}
              />
            </label>
            <label>
              Bill number
              <input
                type="text"
                value={form.bill_number}
                maxLength={100}
                required
                placeholder="As printed on the bill"
                disabled={!canRaise || saving}
                onChange={(event) => setField("bill_number", event.target.value)}
              />
            </label>
            <label>
              Amount (₹)
              <input
                type="number"
                min="0.01"
                step="0.01"
                value={form.amount}
                required
                placeholder="0.00"
                disabled={!canRaise || saving}
                onChange={(event) => setField("amount", event.target.value)}
              />
            </label>
            <label className="ef-form__wide">
              Remarks
              <textarea
                rows={3}
                value={form.remarks}
                maxLength={500}
                placeholder="Optional note for HR"
                disabled={!canRaise || saving}
                onChange={(event) => setField("remarks", event.target.value)}
              />
            </label>
            <label className="ef-form__wide">
              Bill copy
              <input
                key={fileKey}
                type="file"
                accept="image/jpeg,image/png,application/pdf"
                disabled={!canRaise || saving}
                onChange={(event) => setField("attachment", event.target.files?.[0] || null)}
              />
            </label>
            {duplicateExisting ? (
              <div className="ef-duplicate" role="alert">
                <p>
                  You already have a fuel bill on {formatDate(duplicateExisting.bill_date)}
                  {duplicateExisting.bill_number ? ` (${duplicateExisting.bill_number})` : ""}.
                  Submit this one as well only if it is a separate bill.
                </p>
                <button type="button" disabled={saving} onClick={() => submitRequest(true)}>
                  Submit anyway
                </button>
              </div>
            ) : null}
            <div className="ef-form__actions">
              <button type="submit" className="ef-btn ef-btn--primary" disabled={!canRaise || saving}>
                {saving ? "Raising…" : "Raise request"}
              </button>
            </div>
          </form>
        </section>

        <section className="ef-card">
          <h2>How a request moves</h2>
          <ol className="ef-guide">
            <li>
              <strong>Raised</strong>
              <span>You submit the bill date, bill number, and amount.</span>
            </li>
            <li>
              <strong>Pending review</strong>
              <span>The request appears on the Admin and HR dashboard for verification.</span>
            </li>
            <li>
              <strong>Approved</strong>
              <span>The amount is added to your salary for the bill month.</span>
            </li>
            <li>
              <strong>Rejected</strong>
              <span>The request stays here with the reason HR or Admin entered.</span>
            </li>
          </ol>
        </section>
      </div>

      <section className="ef-card">
        <div className="ef-list-head">
          <h2>Your requests</h2>
          <div className="ef-filters" role="tablist" aria-label="Filter by status">
            {FILTERS.map((item) => (
              <button
                key={item.id}
                type="button"
                role="tab"
                aria-selected={filter === item.id}
                className={filter === item.id ? "is-on" : ""}
                onClick={() => setFilter(item.id)}
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>

        {fuelQuery.isLoading ? <p className="ef-empty">Loading your fuel requests…</p> : null}
        {!fuelQuery.isLoading && visibleBills.length === 0 ? (
          <p className="ef-empty">
            {bills.length === 0
              ? "No fuel requests yet. Raise one with the bill date, bill number, and amount."
              : "No requests in this status."}
          </p>
        ) : null}

        <ul className="ef-list">
          {visibleBills.map((bill) => {
            const key = statusKey(bill);
            return (
              <li key={bill.id} className={`ef-request is-${key.toLowerCase()}`}>
                <div className="ef-request__top">
                  <StatusMark status={key} />
                  <strong className="ef-request__amount">{formatMoney(bill.amount)}</strong>
                </div>
                <Cycle status={key} />
                <dl className="ef-facts">
                  <div>
                    <dt>Bill date</dt>
                    <dd>{formatDate(bill.bill_date)}</dd>
                  </div>
                  <div>
                    <dt>Bill number</dt>
                    <dd>{bill.bill_number || "—"}</dd>
                  </div>
                  <div>
                    <dt>Raised</dt>
                    <dd>{formatDate(bill.created_at)}</dd>
                  </div>
                </dl>
                {bill.remarks ? <p className="ef-note">Your note: {bill.remarks}</p> : null}
                {bill.attachment ? (
                  <a className="ef-file" href={mediaUrl(bill.attachment)} target="_blank" rel="noreferrer">
                    View bill copy
                  </a>
                ) : null}
                <Outcome bill={bill} />
                {key === "PENDING" ? (
                  confirmCancelId === bill.id ? (
                    <div className="ef-cancel">
                      <span>Cancel this pending request?</span>
                      <button
                        type="button"
                        className="ef-btn ef-btn--danger"
                        disabled={cancellingId === bill.id}
                        onClick={() => cancelRequest(bill.id)}
                      >
                        {cancellingId === bill.id ? "Cancelling…" : "Yes, cancel"}
                      </button>
                      <button type="button" className="ef-btn" onClick={() => setConfirmCancelId(null)}>
                        Keep it
                      </button>
                    </div>
                  ) : (
                    <button type="button" className="ef-btn" onClick={() => setConfirmCancelId(bill.id)}>
                      Cancel request
                    </button>
                  )
                ) : null}
              </li>
            );
          })}
        </ul>
      </section>
    </div>
  );
}
